import {
  GameData,
  Grapheme,
  Sentence,
  Word,
} from '@/common/types/scopeAndSequence';
import {
  getGraphemesBySpelling,
  getMorphemesByIds,
  getWordsByIds,
} from '@/common/utils/scopeAndSequence';
import { WordSumsCard } from '@/game/GameComponents/PhaserGame/Types/IMinigameCardData';
import { Howl } from 'howler';

const SFX_PATHS = {
  correct: 'correct-sfx.mp3',
  levelUp: 'level-up-sfx.mp3',
  incorrect: 'incorrect-sfx.mp3',
  checkAnswer: 'check-answer.mp3',
  cardDrop: 'card-drop-sfx.mp3',
  click: 'click-sfx.mp3',
  mastered: 'SFX_Letter_Booster_Hint_Full_1.mp3',
  variant: 'variant-sfx.mp3',
  selectVariant: 'select-variant-sfx.mp3',
  phonemeMastered: 'phoneme-mastered-sfx.mp3',
} as const;

const phonemes = [
  'æ',
  'aʊ',
  'b',
  'd',
  'dʒ',
  'e',
  'eɪ',
  'f',
  'g',
  'h',
  'j',
  'juː',
  'k',
  'l',
  'm',
  'n',
  'oʊ',
  'p',
  's',
  't',
  'tʃ',
  'uː',
  'v',
  'w',
  'z',
  'ð',
  'ŋ',
  'ɔ',
  'ɛ',
  'ɪ',
  'ɹ',
  'ʃ',
  'ʊ',
  'ʌ',
  'ʒ',
  'θ',
  'ә',
  'ɛr',
  'ɪr',
  'ɑː',
  'ɔɪ',
  'ɜː',
];

const mysticMatchTips = [
  {
    id: 0,
    audio: 'mystic-match-tip-0.mp3',
  },
];

const wordWeaverTips = [
  {
    id: 0,
    audio: 'word-weaver-tip-0.mp3',
  },
  {
    id: 1,
    audio: 'word-weaver-tip-1.mp3',
  },
];

const instructionFiles = [
  'final-sound.mp3',
  'medial-sound.mp3',
  'whats-next.mp3',
  'turn-the-word.mp3',
  'into.mp3',
  'make-the-word-instruction.mp3',
  'sound.mp3',
  'that-letter-makes-the.mp3',
  'try-again.mp3',
  'mysticMatch-instructions.mp3',
  'wordWeaver-instructions.mp3',
  'sentenceBuilder-instructions.mp3',
  'trickyWords-instructions.mp3',
  'phonemeForge-instructions.mp3',
  'wordRifts-instructions.mp3',
  'leafy-assessment-instructions.mp3',
  'leafy-make-the-word-instruction.mp3',
];

const congratulationsFiles = [
  'good-work.mp3',
  'keep-it-up.mp3',
  'nice.mp3',
  'well-done.mp3',
  'outstanding.mp3',
];

const sfxFiles = [
  'correct-sfx.mp3',
  'level-up-sfx.mp3',
  'incorrect-sfx.mp3',
  'check-answer.mp3',
  'card-drop-sfx.mp3',
  'click-sfx.mp3',
  'SFX_Letter_Booster_Hint_Full_1.mp3',
  'hover-sfx.mp3',
];

interface QueueItem {
  audio: Howl;
  resolve: () => void;
}

interface VolumeSettings {
  masterVolume: number;
  sfxVolume: number;
  instructionsVolume: number;
}
class AudioService {
  sounds: { [key: string]: Howl } = {};
  currentAudio: Howl | null = null;
  music?: Howl;
  currentAudioEndListener: () => void = () => {};
  isPlaying: boolean = false;
  queue: QueueItem[] = [];
  volumeSettings: VolumeSettings = {
    masterVolume: 100,
    sfxVolume: 100,
    instructionsVolume: 100,
  };

  constructor() {
    this.playNextInQueue = this.playNextInQueue.bind(this);
    this.stopAudio = this.stopAudio.bind(this);
  }

  setVolume(settings: Partial<VolumeSettings>) {
    this.volumeSettings = {
      ...this.volumeSettings,
      ...settings,
    };

    // Update volume for all currently loaded sounds
    Object.values(this.sounds).forEach((sound) => {
      if (sound.state() === 'loaded') {
        this.applyVolumeToSound(sound);
      }
    });
  }

  private applyVolumeToSound(
    sound: Howl,
    type: 'sfx' | 'instructions' = 'sfx',
  ) {
    const { masterVolume } = this.volumeSettings;
    const typeVolume =
      type === 'sfx'
        ? this.volumeSettings.sfxVolume
        : this.volumeSettings.instructionsVolume;

    // Convert both volumes from percentage to decimal and multiply
    const finalVolume = (masterVolume / 100) * (typeVolume / 100);
    sound.volume(finalVolume);
  }

  private playNextInQueue() {
    if (this.currentAudio) {
      this.currentAudio.off('end', this.currentAudioEndListener);
    }

    const nextAudioItem = this.queue.shift();

    if (!nextAudioItem) {
      this.isPlaying = false;
      return;
    }

    this.isPlaying = true;
    this.currentAudio = nextAudioItem.audio;

    this.currentAudioEndListener = () => {
      nextAudioItem.resolve();
      this.playNextInQueue();
    };

    this.currentAudio.on('end', this.currentAudioEndListener);
    this.currentAudio.play();
  }

  async playAudio(
    audioSrc: string,
    silent: boolean = false,
    type: 'sfx' | 'instructions' = 'sfx',
  ) {
    return new Promise<void>(async (resolve, reject) => {
      try {
        const audio = await this.loadAudio(audioSrc);
        this.applyVolumeToSound(audio, type);
        this.queue.push({ audio, resolve });
        if (!this.isPlaying) {
          this.playNextInQueue();
        }
      } catch (error) {
        if (!silent) console.error(error);
        reject();
      }
    });
  }

  stopAudio() {
    Howler.stop();
    this.queue = [];
    this.isPlaying = false;
    if (this.currentAudio) {
      this.currentAudio.off('end', this.currentAudioEndListener);
    }
  }

  loadAudio(audioSrc: string) {
    return new Promise<Howl>((resolve, reject) => {
      let audio = this.sounds[audioSrc];
      if (audio) {
        resolve(audio);
      } else {
        audio = new Howl({
          src: [audioSrc],
          preload: true,
          onload: () => {
            this.sounds[audioSrc] = audio;
            resolve(audio);
          },
          onloaderror: () => {
            delete this.sounds[audioSrc];
            reject(`Audio file could not be loaded: ${audioSrc}`);
          },
        });
      }
    });
  }

  playMusic() {
    this.music?.play();
  }
}

const audioService = new AudioService();
export const mentorDialogue = new AudioService();
export const mentorAudioService = new AudioService();

export const setAudioVolume = (
  settings: Partial<{
    masterVolume: number;
    sfxVolume: number;
    instructionsVolume: number;
  }>,
) => {
  audioService.setVolume(settings);
};

export const stopAudio = audioService.stopAudio;

export const gameTips: { [key: string]: { id: number; audio: string }[] } = {
  mysticMatch: mysticMatchTips,
  wordWeaver: wordWeaverTips,
  // Add more games and their corresponding tips arrays as needed.
};

interface AudioToPreload {
  words: Set<string>;
  sentences: Set<string>;
  morphemes: Set<string>;
}

// Helper function to extract words from text
export const extractWords = (text: string): string[] =>
  text
    ? text
        .toLowerCase()
        .replace(/[^\w\s]/g, '')
        .split(/\s+/)
        .filter((word) => word.length > 0)
    : [];

export const collectAudioToPreload = (gameData: GameData): AudioToPreload => {
  const wordsToPreload = new Set<string>();
  const sentencesToPreload = new Set<string>();
  const morphemesToPreload = new Set<string>();

  gameData.wordWeaver?.forEach((word: Word) => {
    wordsToPreload.add(word.spelling.toLowerCase());
    if (word.example) {
      extractWords(word.example).forEach((word) => wordsToPreload.add(word));
    }
  });

  gameData.phonemeForge?.forEach((word: Word) => {
    wordsToPreload.add(word.spelling.toLowerCase());
    if (word.example) {
      extractWords(word.example).forEach((word) => wordsToPreload.add(word));
    }
  });

  // Process 'wordRifts' game data
  gameData.wordRifts?.forEach((item) => {
    wordsToPreload.add(item.spelling.toLowerCase());
    if (item.example) {
      extractWords(item.example).forEach((word) => wordsToPreload.add(word));
    }

    // If the item is a MorphemeWord, extract morphemes
    if ('morphemes' in item && item.morphemes) {
      const morphemes = getMorphemesByIds(item.morphemes);
      morphemes.forEach((morpheme) =>
        morphemesToPreload.add(morpheme.spelling.toLowerCase()),
      );
    }
  });

  // Process 'sentenceBuilder' game data
  gameData.sentenceBuilder?.forEach((sentence: Sentence) => {
    sentencesToPreload.add(sentence.spelling.toLowerCase());
    if (sentence.words) {
      const words = getWordsByIds(sentence.words);
      words.forEach((word) => wordsToPreload.add(word.spelling.toLowerCase()));
    }
  });

  // Process 'trickyWords' game data
  gameData.trickyWords?.forEach((word: Word) =>
    wordsToPreload.add(word.spelling.toLowerCase()),
  );

  return {
    words: wordsToPreload,
    sentences: sentencesToPreload,
    morphemes: morphemesToPreload,
  };
};

export const preloadAllAudio = (audioData: AudioToPreload) => {
  // Preload individual words
  preloadWordAudio(Array.from(audioData.words));

  // Preload sentences
  preloadSentenceAudio(Array.from(audioData.sentences));

  // Preload morphemes
  preloadMorphemeAudio(Array.from(audioData.morphemes));

  // Preload all other audio assets
  preloadPhonemeAudio();
  preloadAudioInstructions();
  preloadCongratulationsAudio();
  preloadSfxAudio();
};
// Function to preload phoneme audio files
export function preloadPhonemeAudio() {
  phonemes.forEach((phoneme) => {
    audioService.loadAudio(`/game_assets/audio/phoneme_audio/${phoneme}.mp3`);
  });
}

export function preloadMorphemeAudio(morphemes: string[]) {
  morphemes.forEach((morpheme) => {
    const processedMorpheme = morpheme.toLowerCase().replace(/[^\w\s]/g, '');
    audioService.loadAudio(
      `/game_assets/morpheme_audio/${processedMorpheme}.mp3`,
    );
  });
}

export function preloadSentenceAudio(sentences: string[]) {
  sentences.forEach((sentence) => {
    const kebabCaseSentence = toKebabCase(sentence);
    audioService.loadAudio(
      `/game_assets/audio/sentences_audio/${kebabCaseSentence}.mp3`,
    );
  });
}

export function preloadWordAudio(words: string[]) {
  words.forEach((word) => {
    audioService.loadAudio(`/game_assets/audio/words_audio/${word}.mp3`);
  });
}

// Refactored function to preload congratulations audio files
export function preloadCongratulationsAudio() {
  congratulationsFiles.forEach((fileName) => {
    audioService.loadAudio(
      `/game_assets/audio/congratulations_audio/${fileName}`,
    );
  });
}

// Refactored function to preload instruction audio files
export function preloadAudioInstructions() {
  instructionFiles.forEach((fileName) => {
    audioService.loadAudio(`/game_assets/audio/audio_instructions/${fileName}`);
  });
}

// Refactored function to preload SFX audio files
export function preloadSfxAudio() {
  sfxFiles.forEach((fileName) => {
    audioService.loadAudio(`/game_assets/audio/sfx/${fileName}`);
  });
}

export function preloadGameTipAudio(gameTitle: string) {
  const tips = gameTips[gameTitle]; // Directly use gameTitle to retrieve the tips array
  if (tips) {
    tips.forEach((tip) => {
      audioService.loadAudio(`/game_assets/audio/game_helpers/${tip.audio}`);
    });
  } else {
    console.error(`No tips found for game title: ${gameTitle}`);
  }
}

export const playGameTipAudio = (gameTitle: string, tipId: number) => {
  const tips = gameTips[gameTitle];
  if (tips) {
    const tip = tips.find((tip) => tip.id === tipId);
    if (tip) {
      const filePath = `/game_assets/audio/game_helpers/${tip.audio}`;
      return audioService.playAudio(filePath);
    }
  }
};

export const playMysticMatchTipAudio = async (
  tipIdx: number,
  additionalData: {
    correctPronunciation: string;
    exampleWord: string;
    position: string;
  },
) => {
  const tips = [
    '/game_assets/audio/game_helpers/mystic-match-tip-0.mp3', // First sound tip
  ];

  const medialFilePath =
    '/game_assets/audio/audio_instructions/medial-sound.mp3';
  const finalFilePath = '/game_assets/audio/audio_instructions/final-sound.mp3';

  switch (additionalData.position) {
    case 'initial':
      const filePath = tips[tipIdx];
      await audioService.playAudio(filePath);
      await playPhonemeAudio(additionalData.correctPronunciation);
      await playWordAudio('for');
      await playWordAudio(additionalData.exampleWord);
      break;

    case 'medial':
      const filePathMedial = tips[tipIdx];
      await audioService.playAudio(filePathMedial);
      await playPhonemeAudio(additionalData.correctPronunciation);
      await audioService.playAudio(medialFilePath);
      await playWordAudio(additionalData.exampleWord);
      break;

    case 'final':
      const filePathFinal = tips[tipIdx];
      await audioService.playAudio(filePathFinal);
      await playPhonemeAudio(additionalData.correctPronunciation);
      await audioService.playAudio(finalFilePath);
      await playWordAudio(additionalData.exampleWord);
      break;

    default:
      return;
  }
};

export const playWordWeaverTipAudio = async (
  tipIdx: number,
  wordToBuild?: string,
) => {
  const tips = [
    '/game_assets/audio/game_helpers/word-weaver-tip-0.mp3', // First sound tip
    '/game_assets/audio/game_helpers/word-weaver-tip-1.mp3', // Next sound tip
    '/game_assets/audio/game_helpers/word-weaver-tip-2.mp3', // Last sound tip
  ];

  const filePath = tips[tipIdx];
  if (filePath) {
    await audioService.playAudio(filePath);
    if (wordToBuild) {
      await playWordAudio(wordToBuild);
    }
  }
};

export const playPhonemeForgeTipAudio = async (
  tipIdx: number,
  baseWord: string,
  wordToCreate: string,
) => {
  const tips = [
    '/game_assets/audio/game_helpers/phoneme-forge-tip-0.mp3', // First sound tip
  ];

  const filePath = tips[tipIdx];
  if (filePath) {
    await audioService.playAudio(filePath);
    await playWordAudio(baseWord);
    await playWordAudio('into');
    await playWordAudio(wordToCreate);
  }
};

export const playSentenceBuilderTipAudio = async (
  tipIdx: number,
  sentence?: string,
) => {
  const tips = [
    '/game_assets/audio/game_helpers/sentence-builder-tip-0.mp3', // First sound tip
    '/game_assets/audio/game_helpers/sentence-builder-tip-1.mp3', // Next sound tip
    '/game_assets/audio/game_helpers/sentence-builder-tip-2.mp3', // Last sound tip
    '/game_assets/audio/game_helpers/sentence-builder-tip-3.mp3', // Last sound tip
  ];
  const filePath = tips[tipIdx];

  switch (tipIdx) {
    case 0:
      await audioService.playAudio(filePath);
      if (sentence) await playSentenceAudio(sentence);
      break;
    case 1:
      await audioService.playAudio(filePath);
      if (sentence) await playSentenceAudio(sentence);
      break;
    case 2:
      await audioService.playAudio(filePath);
      break;
    case 3:
      // await audioService.playAudio(filePath);
      break;
  }
};

export const playWordRiftsTipAudio = async (tipIdx: number) => {
  const tips = [
    '/game_assets/audio/game_helpers/word-rifts-tip-0.mp3', // First sound tip
    '/game_assets/audio/game_helpers/word-rifts-tip-1.mp3', // Next sound tip
  ];
  const filePath = tips[tipIdx];
  if (filePath) {
    await audioService.playAudio(filePath);
  }
};

export const playWordSumsTipAudio = async (tipIdx: number) => {
  const tips = [
    '/game_assets/audio/game_helpers/doubling-instruction.mp3',
    '/game_assets/audio/game_helpers/drop-the-e.mp3',
  ];
  const filePath = tips[tipIdx];
  if (filePath) {
    await audioService.playAudio(filePath);
  }
};

export function playPartialCompletionAudio() {
  const filePath = `/game_assets/audio/audio_instructions/whats-next.mp3`;
  return audioService.playAudio(filePath);
}

export function playPhonemeAudio(phoneme: string | string[]): Promise<void> {
  // Special case for silent e
  if (phoneme === '∅') {
    return new Promise((resolve) => resolve());
  }
  const filePath = `/game_assets/audio/phoneme_audio/${phoneme}.mp3`;
  return audioService.playAudio(filePath, false, 'instructions');
}

export function playTrickyWordAudio(trickyWord: string) {
  const filePath = `/game_assets/audio/words_audio/${trickyWord}.mp3`;
  return audioService.playAudio(filePath, false, 'instructions');
}

export function playExplainerCardInstructions(game: string) {
  const filePath = `/game_assets/audio/audio_instructions/${game}-instructions.mp3`;
  return audioService.playAudio(filePath, false, 'instructions');
}

export function playSentenceAudio(sentence: string) {
  const kebabCaseSentence = toKebabCase(sentence);
  const filePath = `/game_assets/audio/sentences_audio/${kebabCaseSentence.toLowerCase()}.mp3`;
  return audioService.playAudio(filePath, false, 'instructions');
}

export function playInstructions(game: string) {
  const filePath = `/game_assets/audio/audio_instructions/${game}.mp3`;
  return audioService.playAudio(filePath, false, 'instructions');
}

export function makeTheWordAudio() {
  const filePath = `/game_assets/audio/audio_instructions/make-the-word-instruction.mp3`;
  return audioService.playAudio(filePath, false, 'instructions');
}

export function leafyMakeTheWordAudio() {
  const filePath = `/game_assets/audio/assessment/greenleaf-prompts/greenleaf-spell-the-word.mp3`;
  return audioService.playAudio(filePath, false, 'instructions');
}

export function playAssessmentInstructions() {
  const filePath = `/game_assets/audio/audio_instructions/leafy-assessment-instructions.mp3`;
  return audioService.playAudio(filePath, false, 'instructions');
}

export function thatsRightTheSoundIs() {
  const filePath = `/game_assets/audio/audio_instructions/that-right-the-sound-is.mp3`;
  return audioService.playAudio(filePath, false, 'instructions');
}

export async function playCorrectSpelling(correctPhoneme: string) {
  const correctionAudioFilePath = `/game_assets/audio/audio_instructions/card-game-correction.mp3`;
  const phonemeFilePath = `/game_assets/audio/phoneme_audio/${correctPhoneme}.mp3`;

  // First play the correction audio, then the phoneme audio
  await audioService.playAudio(correctionAudioFilePath, false, 'instructions');
  await audioService.playAudio(phonemeFilePath, false, 'instructions');
}

export async function playCorrectWord(correctWord: string) {
  const correctionAudioFilePath = `/game_assets/audio/audio_instructions/card-game-correction.mp3`;
  const phonemeFilePath = `/game_assets/audio/words_audio/${correctWord}.mp3`;

  // First play the correction audio, then the phoneme audio
  await audioService.playAudio(correctionAudioFilePath);
  await audioService.playAudio(phonemeFilePath);
}

export async function getAndPlayAllPhonemes(spelling: string) {
  const graphemes = getGraphemesBySpelling(spelling);
  if (!graphemes.length) {
    console.warn(`No phonemes found for spelling: ${spelling}`);
    return;
  }

  const uniquePhonemes = [...new Set(graphemes.map((g) => g.pronunciation))];

  // Play each phoneme in sequence
  for (const phoneme of uniquePhonemes) {
    await playPhonemeAudio(phoneme);
    // Optional: Add a small delay between phonemes if needed
    await new Promise((resolve) => setTimeout(resolve, 200)); // 500ms delay
  }
}

export async function playNPCDialogue(gameName: string) {
  const genders = ['male', 'female'];
  const maleNames = ['charlie', 'clyde', 'harry', 'jason', 'paul']; // Add all male names here
  const femaleNames = ['domi', 'sarah', 'ines']; // Add all female names here

  const selectedGender = genders[Math.floor(Math.random() * genders.length)];
  const selectedName =
    selectedGender === 'male'
      ? maleNames[Math.floor(Math.random() * maleNames.length)]
      : femaleNames[Math.floor(Math.random() * femaleNames.length)];
  const filePath = `/game_assets/audio/npc-voices/${selectedGender}/${selectedName}-${gameName}.mp3`;
  return audioService.playAudio(filePath);
}

export async function soundOutWord(
  graphemes: Grapheme[],
  audioDelay: number,
  targetGraphemes?: Grapheme[],
  onSoundOut?: (grapheme: Grapheme, index: number, audioDelay: number) => void,
  scene?: Phaser.Scene,
) {
  if (!graphemes?.length) {
    console.warn('No word to sound out');
    return;
  }

  const isVowel = (letter: string): boolean =>
    letter.split('').every((char) => 'aeiou'.includes(char.toLowerCase()));

  const lastLetter = graphemes[graphemes.length - 1];
  const hasSilentE =
    lastLetter?.spelling === 'e' && lastLetter?.pronunciation === '∅';

  const findSecondLastVowel = (): number => {
    for (let i = graphemes.length - 2; i >= 0; i--) {
      if (isVowel(graphemes[i].spelling)) {
        return i;
      }
    }
    return -1;
  };
  const secondLastVowelIndex = hasSilentE ? findSecondLastVowel() : -1;

  for (let i = 0; i < graphemes.length; i++) {
    try {
      // Skip the final silent E
      if (i === graphemes.length - 1 && hasSilentE) {
        continue;
      }

      const currentLetter = graphemes[i];
      if (!currentLetter) continue;

      // Determine which phoneme to play
      const targetLetter = targetGraphemes?.[i];
      let phonemeToPlay = currentLetter.pronunciation;

      // If letters match in target word, use target phoneme
      if (targetLetter && targetLetter.spelling === currentLetter.spelling) {
        phonemeToPlay = targetLetter.pronunciation;
      }

      // Prepare highlighted letters
      const highlightedLetters = [i];

      // Add silent e highlight if applicable
      if (i === secondLastVowelIndex && hasSilentE) {
        highlightedLetters.push(graphemes.length - 1);
      }

      if (scene) {
        await soundOutWordWithDelayedCall(audioDelay, scene);
      } else {
        await soundOutWordWithTimeout(audioDelay);
      }

      // Play the phoneme if it exists
      if (phonemeToPlay) {
        // Then play the phoneme
        onSoundOut?.(currentLetter, i, audioDelay);
        await playPhonemeAudio(phonemeToPlay);
      }
    } catch (error) {
      console.warn(`Error processing phoneme at index ${i}:`, error);
    }
  }
}

// Uses Set Tmeout. This is fine in the dahsboard, but don't use it in the Phaser game.
async function soundOutWordWithTimeout(audioDelay: number) {
  if (audioDelay > 0) {
    await new Promise((resolve) => setTimeout(resolve, audioDelay));
  } else {
    // Add a small delay between phonemes for clarity
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
}

// Uses Phaser's delayedCall, which is compatible with the Phaser game environment.
async function soundOutWordWithDelayedCall(
  audioDelay: number,
  scene: Phaser.Scene,
) {
  if (audioDelay > 0) {
    await new Promise((resolve) => scene.time.delayedCall(audioDelay, resolve));
  } else {
    // Add a small delay between phonemes for clarity
    await new Promise((resolve) => scene.time.delayedCall(50, resolve));
  }
}

export async function soundOutGraphemeWithDelay(
  grapheme: string,
  delay: number,
) {
  await playPhonemeAudio(grapheme);
  await new Promise((resolve) => setTimeout(resolve, delay));
}

export async function soundOutSentenceWordWithDelay(
  word: string,
  delay: number,
) {
  await playWordAudio(word);
  await new Promise((resolve) => setTimeout(resolve, delay));
}

export async function soundOutSentence(sentence: string, audioDelay: number) {
  const words = sentence
    .replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, '')
    .split(' ')
    .filter((word) => word.trim() !== '');

  for (let i = 0; i < words.length; i++) {
    try {
      // Apply audio delay if specified
      if (audioDelay > 0) {
        await new Promise((resolve) => setTimeout(resolve, audioDelay));
      }

      // Play the word audio
      await playWordAudio(words[i].toLowerCase());

      // Small gap between words for clarity
      await new Promise((resolve) => setTimeout(resolve, 100));
    } catch (error) {}
  }
}

export async function soundOutClozeSentence(
  sentence: string,
  missingWord: string,
  audioDelay: number,
  soundOutMissingWord: boolean,
  abortReadoutSignal: AbortSignal,
  onSoundOut?: (word: string, index: number) => void,
) {
  const words = sentence.replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, '').split(' ');

  for (let i = 0; i < words.length; i++) {
    if (abortReadoutSignal.aborted) {
      return;
    }

    const word = words[i];

    // Apply audio delay if specified
    if (audioDelay > 0) {
      await new Promise((resolve) => setTimeout(resolve, audioDelay));
    }

    onSoundOut?.(word, i);

    try {
      if (word === missingWord) {
        if (soundOutMissingWord) {
          await playWordAudio(word.toLowerCase());
        } else {
          await sfx.incorrect();
        }
      } else {
        await playWordAudio(word.toLowerCase());
      }
    } catch (error) {}

    // Small gap between words for clarity
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
}

export async function soundOutMorphemeWord(
  morphemeCards: WordSumsCard[],
  audioDelay: number,
  onSoundOut?: (morpheme: string, index: number) => void,
) {
  for (let i = 0; i < morphemeCards.length; i++) {
    const card = morphemeCards[i];

    // Apply audio delay if specified
    if (audioDelay > 0) {
      await new Promise((resolve) => setTimeout(resolve, audioDelay));
    }

    // Play the morpheme audio
    onSoundOut?.(card.morpheme.spelling, i);
    await playMorphemeAudio(card.morpheme.audioFile ?? card.morpheme.spelling);

    // Add small gap between morphemes for clarity
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
}

function toKebabCase(sentence: string): string {
  // Convert the sentence to lowercase
  const lowerCaseSentence = sentence.toLowerCase();
  // Replace spaces with dashes to convert to kebab case
  // Also, ensure we strip out any non-alphanumeric characters (except for spaces and underscores which we'll convert)
  const kebabCaseSentence = lowerCaseSentence
    .replace(/[^a-z0-9_ ]/g, '')
    .replace(/\s+/g, '-');

  return kebabCaseSentence;
}

export function playSfx(
  soundName: keyof typeof SFX_PATHS,
  isInteraction: boolean = false,
) {
  const filePath = `/game_assets/audio/sfx/${SFX_PATHS[soundName]}`;
  let service = audioService;
  if (isInteraction) {
    // Play interaction sfx in a separate service to avoid interference
    service = new AudioService();
  }
  return service.playAudio(filePath, false, 'sfx');
}

export function playTurnTheWordAudio() {
  const turnTheWordAudioFilePath = `/game_assets/audio/words_audio/turn.mp3`;
  return audioService.playAudio(turnTheWordAudioFilePath);
}

export function playIntoAudio() {
  const intoAudioFilePath = `/game_assets/audio/audio_instructions/into.mp3`;
  return audioService.playAudio(intoAudioFilePath);
}

export function playMakeTheWordAudio() {
  const makeTheWordAudioFilePath = `/game_assets/audio/audio_instructions/make-the-word-instruction.mp3`;
  return audioService.playAudio(makeTheWordAudioFilePath);
}

export function playWordAudio(word: string) {
  const wordAudioFilePath = `/game_assets/audio/words_audio/${word}.mp3`;
  return audioService
    .playAudio(wordAudioFilePath, false, 'instructions')
    .catch(() =>
      audioService.playAudio(
        `/game_assets/audio/morpheme_audio/${word}.mp3`,
        false,
      ),
    )
    .catch(() => {
      console.error('Audio not found for word:', word);
    });
}

export async function playMorphemeAudio(morpheme: string) {
  // Try morpheme_audio, then phoneme_audio, then words_audio
  await audioService
    .playAudio(`/game_assets/audio/morpheme_audio/${morpheme}.mp3`, true)
    .catch(() =>
      audioService.playAudio(
        `/game_assets/audio/phoneme_audio/${morpheme}.mp3`,
        true,
      ),
    )
    .catch(() =>
      audioService.playAudio(
        `/game_assets/audio/words_audio/${morpheme}.mp3`,
        true,
      ),
    )
    .catch(() => {
      console.error('Audio not found for morpheme:', morpheme);
    });
}

export function thatLetterMakesThe() {
  const thatLetterMakesTheFilePath = `/game_assets/audio/audio_instructions/that-letter-makes-the.mp3`;
  return audioService.playAudio(thatLetterMakesTheFilePath);
}

export function soundAudio() {
  const soundAudioFilePath = `/game_assets/audio/audio_instructions/sound.mp3`;
  return audioService.playAudio(soundAudioFilePath);
}

export function playTryAgainAudio() {
  const tryAgainAudioFilePath = `/game_assets/audio/audio_instructions/try-again.mp3`;
  return audioService.playAudio(tryAgainAudioFilePath);
}

export function wellDoneYouMadeTheWord() {
  const wellDoneYouMadeTheWordFilePath = `/game_assets/audio/audio_instructions/well-done-you-made-the-word.mp3`;
  return audioService.playAudio(wellDoneYouMadeTheWordFilePath);
}

export function allWordsCompletedAudio() {
  const wordCompletedFilePath =
    '/game_assets/audio/audio_instructions/well-done-you-made-the-word.mp3';

  audioService.playAudio(wordCompletedFilePath);
}

export function whatsTheNextSoundAudio() {
  const whatsTheNextSoundFilePath = `/game_assets/audio/audio_instructions/whats-the-next-sound.mp3`;
  return audioService.playAudio(whatsTheNextSoundFilePath);
}

export async function playAsInAudio() {
  const asInFilePath = '/game_assets/audio/audio_instructions/as-in.mp3';

  await new Promise((r) => setTimeout(r, 100));
  await audioService.playAudio(asInFilePath);
  await new Promise((r) => setTimeout(r, 100));
}

export const sfx = {
  correct: () => playSfx('correct'),
  levelUp: () => playSfx('levelUp'),
  incorrect: () => playSfx('incorrect'),
  checkAnswer: () => playSfx('checkAnswer', true),
  cardDrop: () => playSfx('cardDrop', true),
  click: () => playSfx('click', true),
  mastered: () => playSfx('mastered'),
  variant: () => playSfx('variant', true),
  selectVariant: () => playSfx('selectVariant', true),
  phonemeMastered: () => playSfx('phonemeMastered'),
};

export function pickRandomCongratulationsAudio() {
  const randomIndex = Math.floor(Math.random() * congratulationsFiles.length);
  const fileName = congratulationsFiles[randomIndex];
  return `/game_assets/audio/congratulations_audio/${fileName}`;
}

export const playRandomCongratulationsAudio = () => {
  const filePath = pickRandomCongratulationsAudio();
  return audioService.playAudio(filePath);
};

export async function playIncorrectFeedback(phoneme: string) {
  const incorrectAudioFilePath = `/game_assets/audio/audio_instructions/that-letter-makes-the.mp3`;
  const phonemeFilePath = `/game_assets/audio/phoneme_audio/${phoneme}.mp3`;
  const soundAudioFilePath = `/game_assets/audio/audio_instructions/sound.mp3`;
  const tryAgainAudioFilePath = `/game_assets/audio/audio_instructions/try-again.mp3`;

  await audioService.playAudio(incorrectAudioFilePath);
  await audioService.playAudio(phonemeFilePath);
  await audioService.playAudio(soundAudioFilePath);
  await audioService.playAudio(tryAgainAudioFilePath);
}

export async function playWordCompleted(word: string) {
  const wordCompletedFilePath =
    '/game_assets/audio/audio_instructions/well-done-you-made-the-word.mp3';
  const wordFilePath = `/game_assets/audio/words_audio/${word}.mp3`;

  // Chain the audio playback using promises
  await audioService.playAudio(wordCompletedFilePath);
  await audioService.playAudio(wordFilePath);
}

export function playClickSound() {
  const clickSoundFilePath = `/game_assets/audio/sfx/click-sfx.mp3`;
  return audioService.playAudio(clickSoundFilePath);
}

export function playRealmIntro(mentor: string) {
  const soundKey = mentorIntros.find((intro) => intro.mentor === mentor);
  if (soundKey) {
    return mentorAudioService.playAudio(realmIntrosPath + soundKey.path);
  }
}

const realmIntrosPath = '/game_assets/audio/mentor_intros/';

const mentorIntros = [
  { mentor: 'Guardian Greenleaf ', path: 'leafy-realm-intro.mp3' },
  { mentor: 'Guardian Springtide', path: 'blossom-realm-intro.mp3' },
  { mentor: 'Guardian Dune', path: 'dune-realm-intro.mp3' },
  { mentor: 'Guardian Avalanche', path: 'frosty-realm-intro.mp3' },
  { mentor: 'Guardian Calypso', path: 'monsoon-realm-intro.mp3' },
];

export function getMusicKeyForBiome(biome: number) {
  return (
    ['fall-1', 'spring-2', 'summer-1', 'winter-1', 'summer-2'][biome] || ''
  );
}

export async function setMusic(url: string) {
  if (audioService.music) {
    await audioService.music.fade(audioService.music.volume(), 0, 1000);
  }
  audioService.music = await audioService.loadAudio(url);
  audioService.playMusic();
}

export async function playSoundsInSequence(
  sounds: { key: string; url: string }[],
  service?: AudioService,
  signal?: AbortSignal,
  onNextSoundPlayed?: (chunk: { key: string; url: string }) => void,
  onSequenceFinished?: () => void,
) {
  const serviceToUse = service || audioService;
  serviceToUse.stopAudio();
  for (let i in sounds) {
    const chunk = sounds[i];
    if (signal?.aborted) return;

    onNextSoundPlayed?.(chunk);
    try {
      await serviceToUse.playAudio(chunk.url + '.mp3');
    } catch (error) {
      continue;
    }
  }
  onSequenceFinished?.();
}

export async function playWordInSentence(word: Word) {
  await playWordAudio(word.spelling);
  await playSentenceAudio(word.example);
  await playWordAudio(word.spelling);
}

export async function playWordDefinition(word: Word) {
  await playWordAudio(word.spelling);
  await playSentenceAudio(word.definition);
  await playWordAudio(word.spelling);
}
